import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import TurnstileWidget from '../components/TurnstileWidget'
import {
  bookingPricingSummary,
  serviceBookingLabel,
  serviceCategoryLabel,
  serviceModuleKey,
  servicePriceLabel,
  servicePricingMode,
} from '../lib/serviceMenu'

const EMPTY_FORM = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  bike_details: '',
  bike_brand: '',
  bike_model: '',
  bike_year: '',
  bike_type: '',
  bike_size: '',
  wheel_setup: '',
  component_serial: '',
  component_make: '',
  component_model: '',
  component_year: '',
  component_details: '',
  preferred_date: '',
  notes: '',
  company_website: '',
}

const SERVICE_GROUP_LABELS = {
  combined: 'Combined Packages',
  fork: 'Fork',
  shock: 'Shock',
  dropper: 'Dropper',
  brake: 'Brake',
  other: 'Other Services',
  'Add-ons': 'Add-ons',
}

const SERVICE_GROUP_ORDER = ['combined', 'fork', 'shock', 'dropper', 'brake', 'Add-ons', 'other']
const SERVICE_MODULE_ORDER = ['suspension', 'general']
const SERVICE_MODULE_LABELS = {
  suspension: 'Suspension Services',
  general: 'General Workshop Services',
}
const BIKE_CATEGORY_OPTIONS = ['', 'XC', 'trail', 'enduro', 'DH', 'eMTB']
const WHEEL_SETUP_OPTIONS = ['', '29', 'mullet', '27.5']

function serviceGroupLabel(group) {
  return SERVICE_GROUP_LABELS[group] || group.replace(/^\w/, c => c.toUpperCase())
}

function serviceGroupRank(group) {
  const index = SERVICE_GROUP_ORDER.indexOf(group)
  return index === -1 ? SERVICE_GROUP_ORDER.length : index
}

function serviceModuleLabel(moduleKey) {
  return SERVICE_MODULE_LABELS[moduleKey] || 'Services'
}

function displayedServicePrice(service, { legacyFixed = false } = {}) {
  const price = Number(service?.base_price || 0)
  const fixedPrice = servicePricingMode(service) === 'fixed'
  if (fixedPrice && price <= 0) return ''
  if (fixedPrice && legacyFixed) return `GBP ${price.toFixed(2)}`
  return service.price_label || servicePriceLabel(service)
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

function todayDate() {
  const d = new Date()
  d.setHours(12, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

// Unset until the Turnstile keys are created; the server check is off until then too.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

function isEmbeddedBooking() {
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1' || window.self !== window.top
  } catch {
    return true
  }
}

export default function BookingPage() {
  const { workshopSlug } = useParams()
  const [workshop, setWorkshop] = useState(null)
  const [days, setDays] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [dateBounds, setDateBounds] = useState({ min: todayDate(), max: '' })
  const [services, setServices] = useState([])
  const [selectedServiceIds, setSelectedServiceIds] = useState([])
  const [serviceModuleFilter, setServiceModuleFilter] = useState('suspension')
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState('all')
  const [serviceSearch, setServiceSearch] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [loading, setLoading] = useState(true)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [createdId, setCreatedId] = useState(null)
  const [embedStep, setEmbedStep] = useState(0)
  const [dateTouched, setDateTouched] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileReset, setTurnstileReset] = useState(0)
  const isEmbed = useMemo(() => isEmbeddedBooking(), [])
  const pageClass = `booking-page${isEmbed ? ' booking-page-embed' : ''}`
  const workshopLogoUrl = workshop?.logo_url || ''

  useEffect(() => {
    loadAvailability(null, true)
  }, [workshopSlug])

  const generalWorkshopEnabled = workshop?.general_workshop_enabled === true
  const showServiceNavigation = generalWorkshopEnabled && services.length > 0
  const availableServiceModules = useMemo(() => {
    if (!generalWorkshopEnabled) return ['suspension']
    return SERVICE_MODULE_ORDER.filter(moduleKey =>
      services.some(service => serviceModuleKey(service) === moduleKey)
    )
  }, [generalWorkshopEnabled, services])
  const serviceSections = useMemo(() => {
    if (!generalWorkshopEnabled) {
      const groups = services.reduce((acc, service) => {
        const key = service.is_addon ? 'Add-ons' : service.category || 'other'
        if (!acc[key]) acc[key] = []
        acc[key].push(service)
        return acc
      }, {})
      return [{
        key: 'suspension',
        title: '',
        groups: Object.entries(groups).sort(([a], [b]) => {
          const rankDiff = serviceGroupRank(a) - serviceGroupRank(b)
          return rankDiff || serviceGroupLabel(a).localeCompare(serviceGroupLabel(b))
        }),
      }]
    }

    return SERVICE_MODULE_ORDER
      .map(moduleKey => {
        const moduleServices = services.filter(service => serviceModuleKey(service) === moduleKey)
        const groups = moduleServices.reduce((acc, service) => {
          const key = moduleKey === 'general'
            ? serviceCategoryLabel(service)
            : service.is_addon ? 'Add-ons' : service.category || 'other'
          if (!acc[key]) acc[key] = []
          acc[key].push(service)
          return acc
        }, {})
        const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
          if (moduleKey === 'suspension') {
            const rankDiff = serviceGroupRank(a) - serviceGroupRank(b)
            return rankDiff || serviceGroupLabel(a).localeCompare(serviceGroupLabel(b))
          }
          return a.localeCompare(b)
        })
        return {
          key: moduleKey,
          title: serviceModuleLabel(moduleKey),
          groups: sortedGroups,
        }
      })
      .filter(section => section.groups.length > 0)
  }, [generalWorkshopEnabled, services])
  const activeServiceSection = serviceSections.find(section => section.key === serviceModuleFilter) || serviceSections[0] || null
  const effectiveCategoryFilter = showServiceNavigation ? serviceCategoryFilter : 'all'
  const serviceCategoryOptions = activeServiceSection
    ? activeServiceSection.groups.map(([group, list]) => ({
      value: group,
      label: activeServiceSection.key === 'general' ? group : serviceGroupLabel(group),
      count: list.length,
    }))
    : []
  const normalisedServiceSearch = showServiceNavigation ? serviceSearch.trim().toLowerCase() : ''
  const visibleServiceGroups = activeServiceSection
    ? activeServiceSection.groups
      .map(([group, list]) => {
        const filtered = list.filter(service => {
          if (effectiveCategoryFilter !== 'all' && group !== effectiveCategoryFilter) return false
          if (!normalisedServiceSearch) return true
          return [
            service.name,
            service.description,
            service.brand,
            service.category,
            serviceCategoryLabel(service),
            service.booking_label || serviceBookingLabel(service),
          ].filter(Boolean).join(' ').toLowerCase().includes(normalisedServiceSearch)
        })
        return [group, filtered]
      })
      .filter(([, list]) => list.length > 0)
    : []
  const visibleServiceCount = visibleServiceGroups.reduce((sum, [, list]) => sum + list.length, 0)
  const selectedServices = services
    .filter(service => selectedServiceIds.includes(service.id))
  const selectedPricing = bookingPricingSummary(selectedServices)
  const selectedHasGeneralService = selectedServices.some(service => serviceModuleKey(service) === 'general')
  const selectedHasSuspensionService = selectedServices.some(service => serviceModuleKey(service) === 'suspension')
  const selectedGeneralOnly = selectedHasGeneralService && !selectedHasSuspensionService
  const selectedMixedModules = selectedHasGeneralService && selectedHasSuspensionService
  const generalContextValues = selectedGeneralOnly
    ? [form.bike_brand, form.bike_model, form.bike_year, form.bike_details, form.component_details]
    : [
      form.bike_brand,
      form.bike_model,
      form.bike_year,
      form.bike_type,
      form.bike_size,
      form.wheel_setup,
      form.bike_details,
      form.component_serial,
      form.component_make,
      form.component_model,
      form.component_year,
      form.component_details,
    ]
  const generalContextProvided = generalContextValues.some(value => String(value || '').trim())
  const contextStepLabel = selectedGeneralOnly
    ? 'Context'
    : selectedMixedModules ? 'Context and part details' : 'Part details'
  const contextStepTitle = selectedGeneralOnly
    ? 'Bike and service context'
    : selectedMixedModules ? 'Context and part details' : 'Part details'
  const selectedFixedTotal = selectedServices
    .reduce((sum, service) => sum + Number(service.base_price || 0), 0)
  const selectedTotalLabel = generalWorkshopEnabled || selectedServices.some(service => service.vat_booking_price)
    ? selectedPricing.total_label
    : selectedFixedTotal > 0 ? `GBP ${selectedFixedTotal.toFixed(2)}` : ''
  const minDate = dateBounds.min || todayDate()
  const maxDate = dateBounds.max || ''
  const selectedDateFull = selectedDay?.date === form.preferred_date && selectedDay.full
  const embedSteps = ['Details', 'Service', contextStepLabel, 'Date']
  const embedStepComplete = [
    Boolean(form.customer_name.trim() && form.customer_email.trim() && form.customer_phone.trim()),
    services.length === 0 || selectedServiceIds.length > 0,
    !selectedHasGeneralService || generalContextProvided,
    Boolean(form.preferred_date && (!isEmbed || dateTouched) && !selectedDateFull),
  ]

  useEffect(() => {
    if (!generalWorkshopEnabled) {
      if (serviceModuleFilter !== 'suspension') setServiceModuleFilter('suspension')
      return
    }
    const fallback = availableServiceModules[0] || 'suspension'
    if (availableServiceModules.length && !availableServiceModules.includes(serviceModuleFilter)) {
      setServiceModuleFilter(fallback)
    }
  }, [availableServiceModules, generalWorkshopEnabled, serviceModuleFilter])

  useEffect(() => {
    setServiceCategoryFilter('all')
  }, [serviceModuleFilter])

  function setField(key, value) {
    setForm(f => ({ ...f, [key]: value }))
  }

  function stepClass(step) {
    return isEmbed && embedStep !== step ? 'booking-step-hidden' : ''
  }

  function nextEmbedStep() {
    if (!embedStepComplete[embedStep]) {
      setError(embedStep === 0
        ? 'Add your name, email and mobile number to continue.'
        : embedStep === 1
          ? 'Choose at least one service to continue.'
          : embedStep === 2
            ? 'Add bike details so the workshop knows what the General service relates to.'
            : 'Choose an available date to continue.')
      return
    }
    setError(null)
    setEmbedStep(step => Math.min(step + 1, embedSteps.length - 1))
  }

  function previousEmbedStep() {
    setError(null)
    setEmbedStep(step => Math.max(step - 1, 0))
  }

  async function loadAvailability(date, initial = false) {
    if (initial) setLoading(true)
    else setAvailabilityLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ slug: workshopSlug })
      if (date) params.set('date', date)
      const res = await fetch(`/.netlify/functions/create-booking-request?${params.toString()}`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Could not load booking page')
      setWorkshop(data.workshop)
      setDays(data.days || [])
      setSelectedDay(data.selected_day || null)
      setDateBounds({ min: data.min_date || todayDate(), max: data.max_date || '' })
      if (data.services) setServices(data.services || [])
      setForm(f => ({
        ...f,
        preferred_date: date || f.preferred_date || (isEmbed ? '' : data.selected_day?.date || ''),
      }))
    } catch (err) {
      setError(err.message)
    }
    if (initial) setLoading(false)
    else setAvailabilityLoading(false)
  }

  function setPreferredDate(value) {
    setDateTouched(Boolean(value))
    setField('preferred_date', value)
    loadAvailability(value)
  }

  function toggleService(serviceId) {
    setSelectedServiceIds(ids =>
      ids.includes(serviceId) ? ids.filter(id => id !== serviceId) : [...ids, serviceId]
    )
  }

  async function submit(e) {
    e.preventDefault()
    setError(null)
    if (!form.preferred_date || (isEmbed && !dateTouched)) {
      setError('Choose an available date before sending your booking request.')
      return
    }
    if (selectedDateFull) {
      setError('That date is full, please pick another.')
      return
    }
    if (selectedHasGeneralService && !generalContextProvided) {
      setError('Add bike details so the workshop knows what the General service relates to.')
      return
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the security check before sending your request.')
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/.netlify/functions/create-booking-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          service_ids: selectedServiceIds,
          vat_booking_prices: selectedServices.map(service => service.vat_booking_price).filter(Boolean),
          workshop_slug: workshopSlug,
          turnstile_token: turnstileToken,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Could not submit booking request')
      setCreatedId(data.id)
    } catch (err) {
      setError(err.message)
      // Tokens are single-use, so a retry needs a fresh check.
      setTurnstileToken('')
      setTurnstileReset(count => count + 1)
    }
    setSubmitting(false)
  }

  if (loading) {
    return (
      <div className={pageClass}>
        <div className="booking-shell">
          <span className="spinner" style={{ width: 22, height: 22 }} />
        </div>
      </div>
    )
  }

  if (error && !workshop) {
    return (
      <div className={pageClass}>
        <div className="booking-shell">
          <div className="booking-brand">Rebound</div>
          <div className="alert alert-error">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className={pageClass}>
      <div className="booking-shell">
        <div className={`booking-brand ${workshopLogoUrl ? 'booking-brand-with-logo' : ''}`}>
          {workshopLogoUrl && <img src={workshopLogoUrl} alt={`${workshop?.name || 'Workshop'} logo`} />}
          <div>{workshop?.name || 'Workshop'} <span>Booking</span></div>
        </div>
        {isEmbed && !createdId && (
          <>
            <div className="booking-widget-progress">
              {embedSteps.map((step, index) => (
                <button
                  type="button"
                  key={step}
                  className={`${index === embedStep ? 'active' : ''} ${index < embedStep ? 'done' : ''}`}
                  onClick={() => {
                    if (index <= embedStep || embedStepComplete.slice(0, index).every(Boolean)) {
                      setError(null)
                      setEmbedStep(index)
                    }
                  }}
                >
                  <span>{index + 1}</span>{step}
                </button>
              ))}
            </div>
            {selectedServices.length > 0 && (
              <div className="booking-widget-selected">
                <span>{selectedServices.map(service => service.name).join(' + ')}</span>
                {selectedTotalLabel && <strong>{selectedTotalLabel}</strong>}
              </div>
            )}
          </>
        )}

        {createdId ? (
          <div className="booking-confirm">
            <div className="booking-confirm-title">Request received</div>
            <p>{workshop?.name || 'The workshop'} will confirm your booking shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            {error && <div className="alert alert-error">{error}</div>}
            <div style={{ position: 'absolute', left: '-10000px', height: 1, width: 1, overflow: 'hidden' }} aria-hidden="true">
              <label>Company Website</label>
              <input
                tabIndex="-1"
                autoComplete="off"
                value={form.company_website}
                onChange={e => setField('company_website', e.target.value)}
              />
            </div>

            <div className={stepClass(0)}>
              {isEmbed && <div className="booking-widget-title">Your details</div>}
            <div className="grid-2">
              <div className="field">
                <label>Full Name</label>
                <input value={form.customer_name} onChange={e => setField('customer_name', e.target.value)} required />
              </div>
              <div className="field">
                <label>Email</label>
                <input type="email" value={form.customer_email} onChange={e => setField('customer_email', e.target.value)} required />
              </div>
            </div>

            <div className="field">
              <label>Mobile Number</label>
              <input type="tel" value={form.customer_phone} onChange={e => setField('customer_phone', e.target.value)} required />
            </div>
            </div>

            <div className={stepClass(1)}>
              {isEmbed && <div className="booking-widget-title">Choose your service</div>}
            {services.length > 0 && (
              <div className="booking-services">
                <label>Services</label>
                {showServiceNavigation && availableServiceModules.length > 1 && (
                  <div className="booking-service-tabs">
                    {availableServiceModules.map(moduleKey => (
                      <button
                        type="button"
                        key={moduleKey}
                        className={serviceModuleFilter === moduleKey ? 'active' : ''}
                        onClick={() => setServiceModuleFilter(moduleKey)}
                      >
                        {serviceModuleLabel(moduleKey)}
                      </button>
                    ))}
                  </div>
                )}
                {showServiceNavigation && (
                  <div className="booking-service-tools">
                    <input
                      type="search"
                      value={serviceSearch}
                      onChange={e => setServiceSearch(e.target.value)}
                      placeholder="Search services..."
                      aria-label="Search services"
                    />
                    {serviceCategoryOptions.length > 1 && (
                      <div className="booking-service-filters">
                        <button
                          type="button"
                          className={serviceCategoryFilter === 'all' ? 'active' : ''}
                          onClick={() => setServiceCategoryFilter('all')}
                        >
                          All <span>{serviceCategoryOptions.reduce((sum, option) => sum + option.count, 0)}</span>
                        </button>
                        {serviceCategoryOptions.map(option => (
                          <button
                            type="button"
                            key={option.value}
                            className={serviceCategoryFilter === option.value ? 'active' : ''}
                            onClick={() => setServiceCategoryFilter(option.value)}
                          >
                            {option.label} <span>{option.count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {showServiceNavigation && selectedServices.length > 0 && (
                  <div className="booking-service-selection">
                    <span>{selectedServices.length} selected</span>
                    <strong>{selectedServices.map(service => service.name).join(' + ')}</strong>
                  </div>
                )}
                {activeServiceSection && (
                  <div className={activeServiceSection.title ? 'booking-service-section' : ''}>
                    {activeServiceSection.title && <div className="booking-service-module">{activeServiceSection.title}</div>}
                    {visibleServiceGroups.length > 0 ? visibleServiceGroups.map(([group, list]) => (
                      <div key={`${activeServiceSection.key}-${group}`} style={{ marginBottom: 12 }}>
                        <div className="booking-service-group">{activeServiceSection.key === 'general' ? group : serviceGroupLabel(group)}</div>
                        {list.map(service => {
                          const priceLabel = displayedServicePrice(service, { legacyFixed: !generalWorkshopEnabled })
                          const bookingLabel = service.booking_label || serviceBookingLabel(service)
                          return (
                            <label key={service.id} className="booking-service">
                              <input
                                type="checkbox"
                                checked={selectedServiceIds.includes(service.id)}
                                onChange={() => toggleService(service.id)}
                              />
                              <span>
                                <strong>{service.name}</strong>
                                {service.description && <small>{service.description}</small>}
                                {bookingLabel && (
                                  <span className="booking-service-tags">
                                    <span className="booking-service-badge">{bookingLabel}</span>
                                  </span>
                                )}
                              </span>
                              {priceLabel && <em>{priceLabel}</em>}
                            </label>
                          )
                        })}
                      </div>
                    )) : (
                      <div className="booking-service-empty">No services match the current filter.</div>
                    )}
                  </div>
                )}
                {showServiceNavigation && normalisedServiceSearch && <div className="booking-service-count">{visibleServiceCount} matching service{visibleServiceCount === 1 ? '' : 's'}</div>}
                {generalWorkshopEnabled && selectedPricing.summary_label && (
                  <div className="booking-service-total">
                    <span>{selectedPricing.pricing_mode === 'fixed' ? 'Estimated total' : 'Booking estimate'}</span>
                    <strong>{selectedPricing.pricing_mode === 'fixed' ? selectedPricing.total_label : selectedPricing.summary_label}</strong>
                  </div>
                )}
                {!generalWorkshopEnabled && selectedFixedTotal > 0 && (
                  <div className="booking-service-total">
                    Estimated total <strong>GBP {selectedFixedTotal.toFixed(2)}</strong>
                  </div>
                )}
              </div>
            )}
            </div>

            <div className={stepClass(2)}>
              {isEmbed && <div className="booking-widget-title">{contextStepTitle}</div>}
            {selectedGeneralOnly && (
              <div className="alert alert-info" style={{ marginBottom: 14 }}>
                Tell the workshop what bike, item, area, or problem this service relates to.
              </div>
            )}
            {selectedMixedModules && (
              <div className="alert alert-info" style={{ marginBottom: 14 }}>
                Add bike or service context for the General service. Part details are also useful for the Suspension service if you know them.
              </div>
            )}
            <div className="grid-3">
              <div className="field">
                <label>Bike Brand</label>
                <input value={form.bike_brand} onChange={e => setField('bike_brand', e.target.value)} placeholder="Santa Cruz, Trek..." />
              </div>
              <div className="field">
                <label>Bike Model</label>
                <input value={form.bike_model} onChange={e => setField('bike_model', e.target.value)} />
              </div>
              <div className="field">
                <label>Bike Year</label>
                <input value={form.bike_year} onChange={e => setField('bike_year', e.target.value)} inputMode="numeric" />
              </div>
            </div>

            {selectedGeneralOnly ? (
              <div className="grid-2">
                <div className="field">
                  <label>Bike / Context Details</label>
                  <textarea
                    value={form.bike_details}
                    onChange={e => setField('bike_details', e.target.value)}
                    placeholder="Tell us what bike, area of the bike, or issue this service relates to."
                  />
                </div>
                <div className="field">
                  <label>Component / Part Details (optional)</label>
                  <textarea
                    value={form.component_details}
                    onChange={e => setField('component_details', e.target.value)}
                    placeholder="Please provide as much information as possible about the components or parts on your bike."
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="grid-3">
                  <div className="field">
                    <label>Bike Category</label>
                    <select value={form.bike_type} onChange={e => setField('bike_type', e.target.value)}>
                      {BIKE_CATEGORY_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select category...'}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>Bike Size</label>
                    <input value={form.bike_size} onChange={e => setField('bike_size', e.target.value)} placeholder="M, L, XL..." />
                  </div>
                  <div className="field">
                    <label>Wheel Setup</label>
                    <select value={form.wheel_setup} onChange={e => setField('wheel_setup', e.target.value)}>
                      {WHEEL_SETUP_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select wheel setup...'}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="field">
                    <label>{selectedMixedModules ? 'Bike / Context Details' : 'Other Bike Details'}</label>
                    <textarea
                      value={form.bike_details}
                      onChange={e => setField('bike_details', e.target.value)}
                      placeholder={selectedMixedModules ? 'Tell us what bike, area of the bike, or issue the General service relates to.' : ''}
                    />
                  </div>
                  <div className="field">
                    <label>{selectedMixedModules ? 'Part Serial Number (if known)' : 'Part Serial Number'}</label>
                    <input value={form.component_serial} onChange={e => setField('component_serial', e.target.value)} />
                  </div>
                </div>

                <div className="grid-3">
                  <div className="field">
                    <label>{selectedMixedModules ? 'Part Make (if known)' : 'Part Make'}</label>
                    <input value={form.component_make} onChange={e => setField('component_make', e.target.value)} placeholder={selectedMixedModules ? 'Shimano, SRAM, Fox...' : 'Fox, RockShox...'} />
                  </div>
                  <div className="field">
                    <label>{selectedMixedModules ? 'Part Model (if known)' : 'Part Model'}</label>
                    <input value={form.component_model} onChange={e => setField('component_model', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>{selectedMixedModules ? 'Part Year (if known)' : 'Part Year'}</label>
                    <input value={form.component_year} onChange={e => setField('component_year', e.target.value)} inputMode="numeric" />
                  </div>
                </div>

                <div className="field">
                  <label>Other Part Details</label>
                  <textarea
                    value={form.component_details}
                    onChange={e => setField('component_details', e.target.value)}
                    placeholder={selectedMixedModules ? 'Please provide as much information as possible about the components or parts on your bike.' : ''}
                  />
                </div>
              </>
            )}
            </div>

            <div className={stepClass(3)}>
              {isEmbed && <div className="booking-widget-title">Pick a date</div>}
            <div className="field">
              <label>Preferred Date</label>
              <input
                type="date"
                value={form.preferred_date}
                min={minDate}
                max={maxDate}
                onChange={e => setPreferredDate(e.target.value)}
                required
              />
              {selectedDay?.date === form.preferred_date && (
                <div className={`booking-date-status ${selectedDay.full ? 'full' : ''}`}>
                  {selectedDay.full
                    ? 'That date is full. Choose one of the nearby available slots below.'
                    : `${selectedDay.remaining || 1} slot${selectedDay.remaining === 1 ? '' : 's'} available on this date.`}
                </div>
              )}
            </div>

            {(availabilityLoading || days.length > 0) && (
              <div className="booking-slot-panel">
                <div className="booking-slot-title">Next available slots near your date</div>
                {availabilityLoading ? (
                  <div className="text-muted fs12"><span className="spinner" /> Checking availability...</div>
                ) : (
                  <div className="booking-days">
                    {days.map(day => (
                      <button
                        type="button"
                        key={day.date}
                        className={`booking-day ${form.preferred_date === day.date ? 'active' : ''}`}
                        onClick={() => setPreferredDate(day.date)}
                      >
                        <span>{formatDate(day.date)}</span>
                        <small>{day.remaining || 1} slot{day.remaining === 1 ? '' : 's'} open</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="field">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} />
            </div>
            </div>

            {TURNSTILE_SITE_KEY && (!isEmbed || embedStep === embedSteps.length - 1) && (
              <TurnstileWidget siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} onError={setError} resetKey={turnstileReset} />
            )}

            {isEmbed ? (
              <div className="booking-widget-nav">
                <button className="btn btn-ghost" type="button" onClick={previousEmbedStep} disabled={embedStep === 0 || submitting}>Back</button>
                {embedStep < embedSteps.length - 1 ? (
                  <button className="btn btn-primary" type="button" onClick={nextEmbedStep}>Next</button>
                ) : (
                  <button className="btn btn-primary" type="submit" disabled={submitting || !form.preferred_date || (isEmbed && !dateTouched) || selectedDateFull}>
                    {submitting ? <><span className="spinner" /> Sending...</> : 'Send Request'}
                  </button>
                )}
              </div>
            ) : (
              <button className="btn btn-primary btn-full" type="submit" disabled={submitting || !form.preferred_date || selectedDateFull}>
                {submitting ? <><span className="spinner" /> Sending...</> : 'Send Booking Request'}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
